import { Injectable, DOCUMENT, Inject } from '@angular/core';
import {
  WithSubscriptionComponent,
  UserService,
  LogonService
} from '@sabstravtech/obtservices/angular';

import { LightningUserFavorurite } from '../../core/classes/user-favourite.enum';
import { IDashboardInfo } from '../../core/interfaces/dashboard.interface';

export enum ThemeNames {
  DEFAULT = 'theme--default',
  SCION = 'theme--scion',
  BLUE = 'theme--blue-theme',
  ARRANGEMY = 'theme--arrangemy-theme',
  SIMPLEXITY = 'theme--simplexity-theme'
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService extends WithSubscriptionComponent {
  isLightningEnv = true;
  isScionEnv = false;
  themeName: string;
  lightningMyBookingsImageURL =
    'https://images.sabscorp.com/images/enterprise/lightUK/assets/images/lightning_logo.svg';
  scionMyBookingsImageURL = 'https://images.sabscorp.com/images/sabsscion/sabstt/SABS-logo.png';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private userService: UserService,
    private logonService: LogonService
  ) {
    super();
    this.isLightningEnv = this.logonService.isLightningEnv;
    this.isScionEnv = this.logonService.isScionEnv;
  }

  setTheme(): void {
    this.subscribe(this.userService.changeUser, () => this.updateTheme());
    this.updateTheme();
  }

  updateTheme(): void {
    const SET_THEME_NAME = this.isScionEnv
      ? this.getScionTheme()
      : this.userService.getUserFavoriteValue(LightningUserFavorurite.themename) ||
        ThemeNames.DEFAULT;
    this.themeName = SET_THEME_NAME;
    const htmlTag = this.document.getElementsByTagName('html')[0];
    htmlTag.className = '';
    htmlTag.className = SET_THEME_NAME;
    this.updateFavicon();
    // this.updateBackgroundImage();
  }

  private updateFavicon(): void {
    const faviconUrl = this.isLightningEnv
      ? 'favicon.ico'
      : 'https://images.sabscorp.com/images/logo/favicon-scion.ico';
    const link: HTMLLinkElement =
      this.document.querySelector('link[rel="icon"]') || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = faviconUrl;
    document.head.appendChild(link);
  }

  getLogoMyBookings(): string {
    let defaultLogoUrl: string;
    if (this.isLightningEnv && this.themeName === ThemeNames.DEFAULT) {
      defaultLogoUrl =
        'https://images.sabscorp.com/images/enterprise/lightUK/assets/images/lightning_logo.svg';
    } else if (this.isLightningEnv) {
      defaultLogoUrl =
        'https://images.sabscorp.com/images/enterprise/lightUK/assets/images/lightning_logo_dark.svg';
    } else {
      defaultLogoUrl = this.scionMyBookingsImageURL;
    }
    return defaultLogoUrl;
  }

  getLogo(): string {
    const logoUrl: { imageSrc: string } = this.userService.getUserFavoriteObject(
      LightningUserFavorurite.ClientCompanyLogo
    );
    let defaultLogoUrl: string;
    if (this.isLightningEnv) {
      defaultLogoUrl = 'https://images.sabscorp.com/images/enterprise/lightUK/lightningLogo.svg';
    } else {
      this.subscribe(this.userService.fullUserDetails.userConfigs, (data) => {
        const dashboardInfo: IDashboardInfo = this.userService.getUserFavoriteObject(
          LightningUserFavorurite.scionDashboardInfo
        );
        defaultLogoUrl =
          dashboardInfo && dashboardInfo.dashboardImage
            ? dashboardInfo.dashboardImage
            : 'https://images.sabscorp.com/images/sabsscion/sabstt/SABS-logo.png';
      });
    }
    return logoUrl?.imageSrc || defaultLogoUrl;
  }

  private updateBackgroundImage(): void {
    // in progress
    let backgroundImageUrl: string;
    let backgroundColor: string;
    if (this.isScionEnv) {
      backgroundImageUrl = '';
      backgroundColor = '#f5f5f5';
    } else {
      backgroundImageUrl =
        'https://images.sabscorp.com/images/enterprise/lightUK/assets/images/bg_img.jpg';
      backgroundColor = '#19514e';
    }
    const loadingScreenContainer = this.document.querySelector(
      '.loading-screen-container'
    ) as HTMLElement;
    if (loadingScreenContainer) {
      loadingScreenContainer.style.backgroundImage = `url('${backgroundImageUrl}')`;
      loadingScreenContainer.style.backgroundColor = `url('${backgroundColor}')`;
    }
  }

  getScionTheme(): string {
    const tmcName = this.userService.fullUserDetails.userData.value?.tmc_name;
    if (tmcName?.toLowerCase() === 'arrangemy') {
      // Going off the tmc names for now, as discussed on standup may change this to admin panel functionality in the future
      return ThemeNames.ARRANGEMY;
    } else if (tmcName?.toLowerCase() === 'simplexity travel') {
      return ThemeNames.SIMPLEXITY;
    } else {
      return ThemeNames.SCION;
    }
  }

  getRedPreferredClientIcon(): string {
    if (this.isScionEnv) {
      if (this.themeName === ThemeNames.ARRANGEMY) {
        return 'hotel-map-pin-red-heart-arrangemy.png';
      }
      if (this.themeName === ThemeNames.SIMPLEXITY) {
        return 'hotel-map-pin-red-heart-simplexity.png';
      } else {
        return 'hotel-map-pin-red-heart-blue.png';
      }
    } else {
      if (this.themeName === ThemeNames.BLUE) {
        return 'hotel-map-pin-green-heart-blue.png';
      } else {
        return 'hotel-map-pin-green-heart.png';
      }
    }
  }

  getRedPreferredConsumerIcon(): string {
    if (this.isScionEnv) {
      if (this.themeName === ThemeNames.ARRANGEMY) {
        return 'hotel-map-pin-red-heart-arrangemy.png';
      }
      if (this.themeName === ThemeNames.SIMPLEXITY) {
        return 'hotel-map-pin-red-heart-simplexity.png';
      } else {
        return 'hotel-map-pin-red-heart-blue.png';
      }
    } else {
      return 'hotel-map-pin-red-ctm.png';
    }
  }

  getOrangePreferredClientIcon(): string {
    if (this.isScionEnv) {
      if (this.themeName === ThemeNames.ARRANGEMY) {
        return 'hotel-map-pin-orange-heart-arrangemy.png';
      }
      if (this.themeName === ThemeNames.SIMPLEXITY) {
        return 'hotel-map-pin-orange-heart-simplexity.png';
      } else {
        return 'hotel-map-pin-orange-heart-blue.png';
      }
    } else {
      if (this.themeName === ThemeNames.BLUE) {
        return 'hotel-map-pin-orange-heart-blue.png';
      } else {
        return 'hotel-map-pin-orange-heart.png';
      }
    }
  }

  getOrangePreferredConsumerIcon(): string {
    if (this.isScionEnv) {
      if (this.themeName === ThemeNames.ARRANGEMY) {
        return 'hotel-map-pin-orange-heart-arrangemy.png';
      }
      if (this.themeName === ThemeNames.SIMPLEXITY) {
        return 'hotel-map-pin-orange-heart-simplexity.png';
      } else {
        return 'hotel-map-pin-orange-heart-blue.png';
      }
    } else {
      return 'hotel-map-pin-orange-ctm.png';
    }
  }

  getGreenPreferredClientIcon(): string {
    if (this.isScionEnv) {
      if (this.themeName === ThemeNames.ARRANGEMY) {
        return 'hotel-map-pin-green-heart-arrangemy.png';
      }
      if (this.themeName === ThemeNames.SIMPLEXITY) {
        return 'hotel-map-pin-green-heart-simplexity.png';
      } else {
        return 'hotel-map-pin-green-heart-blue.png';
      }
    } else {
      if (this.themeName === ThemeNames.BLUE) {
        return 'hotel-map-pin-green-heart-blue.png';
      } else {
        return 'hotel-map-pin-green-heart.png';
      }
    }
  }

  getGreenPreferredConsumerIcon(): string {
    if (this.isScionEnv) {
      if (this.themeName === ThemeNames.ARRANGEMY) {
        return 'hotel-map-pin-green-heart-arrangemy.png';
      }
      if (this.themeName === ThemeNames.SIMPLEXITY) {
        return 'hotel-map-pin-green-heart-simplexity.png';
      } else {
        return 'hotel-map-pin-green-heart-blue.png';
      }
    } else {
      return 'hotel-map-pin-green-ctm.png';
    }
  }

  getMapClusterIcon(): string {
    if (this.isScionEnv) {
      if (this.themeName === ThemeNames.ARRANGEMY) {
        return 'mark-cluster-arrangemy.png';
      }
      if (this.themeName === ThemeNames.SIMPLEXITY) {
        return 'mark-cluster-simplexity.png';
      } else {
        return 'mark-cluster-blue.png';
      }
    } else {
      if (this.themeName === ThemeNames.BLUE) {
        return 'mark-cluster-blue.png';
      } else {
        return 'mark-cluster.png';
      }
    }
  }
}
